import type { AllProductPrecheckSelectionDecision } from "../api/models";

type PrecheckMatchCandidate = {
    productText: string;
    displayText?: string;
    manual_key?: string;
    faq_key?: string;
    count?: number;
    productVersion?: {
        manual_version_key: string;
        faq_version_key: string;
    };
    subProduct?: {
        manual_sub_key: string;
        faq_sub_key: string;
    };
};

type PrecheckCatalogProduct = {
    text?: string;
    manual_key?: unknown;
    faq_key?: unknown;
    manual_groupids?: unknown[];
    faq_groupids?: unknown[];
    versions?: Array<{
        text?: string;
        manual_version_key?: unknown;
        faq_version_key?: unknown;
        sub_versions?: Array<{
            text?: string;
            manual_sub_key?: unknown;
            faq_sub_key?: unknown;
        }>;
    }>;
};

const normalizeSelectionLabel = (value: unknown): string =>
    String(value ?? "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();

const normalizeSelectionKey = (value: unknown): string =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const normalizedKeySet = (values: unknown[] | null | undefined): Set<string> => new Set((values ?? []).map(normalizeSelectionKey).filter(Boolean));

const hasMatchingKey = (values: unknown[], expected: Set<string>): boolean =>
    expected.size === 0 || values.some(value => expected.has(normalizeSelectionKey(value)));

const resolveFromCatalog = <T extends PrecheckMatchCandidate>(
    selectionDecision: AllProductPrecheckSelectionDecision,
    catalogProducts: PrecheckCatalogProduct[],
    productIndexes: Set<string>,
    versionIndexes: Set<string>,
    subProductIndexes: Set<string>
): T | null => {
    const productLabel = normalizeSelectionLabel(selectionDecision.ptf_selected_product_label ?? selectionDecision.selected_product_label);
    const matchingProducts = catalogProducts.filter(product => {
        const productKeys = [product.manual_key, product.faq_key, ...(product.manual_groupids ?? []), ...(product.faq_groupids ?? [])];
        return (productIndexes.size > 0 && hasMatchingKey(productKeys, productIndexes)) || normalizeSelectionLabel(product.text) === productLabel;
    });
    if (matchingProducts.length !== 1) return null;

    const product = matchingProducts[0];
    const versions = Array.isArray(product.versions) ? product.versions : [];
    const version =
        versionIndexes.size > 0 ? versions.find(item => hasMatchingKey([item.manual_version_key, item.faq_version_key], versionIndexes)) : undefined;
    if (versionIndexes.size > 0 && !version) return null;

    const subProduct =
        subProductIndexes.size > 0
            ? (version?.sub_versions ?? []).find(item => hasMatchingKey([item.manual_sub_key, item.faq_sub_key], subProductIndexes))
            : undefined;
    if (subProductIndexes.size > 0 && !subProduct) return null;

    return {
        productText: String(product.text ?? selectionDecision.ptf_selected_product_label ?? selectionDecision.selected_product_label ?? ""),
        displayText: String(selectionDecision.ptf_selected_display_label ?? selectionDecision.selected_display_label ?? ""),
        manual_key: String(product.manual_key ?? ""),
        faq_key: String(product.faq_key ?? ""),
        count: Number(selectionDecision.ptf_selected_citation_count ?? selectionDecision.selected_citation_count ?? 0),
        ...(version
            ? {
                  productVersion: {
                      manual_version_key: String(version.manual_version_key ?? ""),
                      faq_version_key: String(version.faq_version_key ?? "")
                  }
              }
            : {}),
        ...(subProduct
            ? {
                  subProduct: {
                      manual_sub_key: String(subProduct.manual_sub_key ?? ""),
                      faq_sub_key: String(subProduct.faq_sub_key ?? "")
                  }
              }
            : {})
    } as T;
};

export const resolvePrecheckAutoSelectedProduct = <T extends PrecheckMatchCandidate>(
    matchedProducts: T[],
    selectionDecision?: AllProductPrecheckSelectionDecision | null,
    catalogProducts: PrecheckCatalogProduct[] = []
): T | null => {
    if (!selectionDecision || selectionDecision.action !== "auto_select") {
        return null;
    }

    // Use the PTF-effective pick (post customer-config override) when present, so an entitled
    // customer's registered version is what actually gets applied, not the model's raw guess.
    const selectedDisplayLabel = normalizeSelectionLabel(selectionDecision.ptf_selected_display_label ?? selectionDecision.selected_display_label);
    const selectedProductLabel = normalizeSelectionLabel(selectionDecision.ptf_selected_product_label ?? selectionDecision.selected_product_label);
    const productIndexes = normalizedKeySet(selectionDecision.ptf_selected_product_indexes ?? selectionDecision.selected_product_indexes);
    const versionIndexes = normalizedKeySet(selectionDecision.ptf_selected_version_indexes ?? selectionDecision.selected_version_indexes);
    const subProductIndexes = normalizedKeySet(selectionDecision.ptf_selected_sub_product_indexes ?? selectionDecision.selected_sub_product_indexes);

    if (productIndexes.size > 0) {
        const keyMatch = matchedProducts.find(
            product =>
                hasMatchingKey([product.manual_key, product.faq_key], productIndexes) &&
                hasMatchingKey([product.productVersion?.manual_version_key, product.productVersion?.faq_version_key], versionIndexes) &&
                hasMatchingKey([product.subProduct?.manual_sub_key, product.subProduct?.faq_sub_key], subProductIndexes) &&
                (versionIndexes.size > 0 ? !!product.productVersion : true) &&
                (subProductIndexes.size > 0 ? !!product.subProduct : !product.subProduct)
        );
        if (keyMatch) return keyMatch;

        const catalogMatch = resolveFromCatalog<T>(selectionDecision, catalogProducts, productIndexes, versionIndexes, subProductIndexes);
        if (catalogMatch) return catalogMatch;
    }

    if (selectedDisplayLabel) {
        const exactDisplayMatch = matchedProducts.find(product => normalizeSelectionLabel(product.displayText ?? product.productText) === selectedDisplayLabel);
        if (exactDisplayMatch) {
            return exactDisplayMatch;
        }
    }

    if (selectedProductLabel) {
        const productLabelMatches = matchedProducts.filter(product => normalizeSelectionLabel(product.productText) === selectedProductLabel);
        if (productLabelMatches.length === 1) {
            return productLabelMatches[0];
        }
    }

    return null;
};
